import React, { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

const FULL_DUR = 10.05;  // intro clip duration (ffprobe) (s)
const END_LEAD = 0.5;    // reveal the login while the clip fades to black (s)
const REVEAL = 500;      // short dissolve from the black tail into the login (ms)
const SKIP_FADE = 320;   // quick fade on "Passer" (ms)
const MASTER_MAX = 12500; // absolute safety: always reveal the form (ms)

// One pre-composed clip: seq1 (energy -> green eye) then the 3 requested shots
// (extreme close -> medium bust -> wide scene), crossfaded together in ffmpeg.
// A single <video> avoids the multi-video buffering aborts, then we dissolve
// gently into the login form (which shares the same samurai backdrop).
export const HeroSection = ({ isActive, onFinish, onReveal }) => {
  const pub = process.env.PUBLIC_URL || "";
  const vRef = useRef(null);
  const [fading, setFading] = useState(false);
  const cbRef = useRef({ onFinish, onReveal });
  cbRef.current = { onFinish, onReveal };
  const skipRef = useRef(() => {});

  useEffect(() => {
    if (!isActive) return;
    const v = vRef.current;
    const timers = [];
    let raf = 0;
    let done = false;

    const play = (el) => {
      if (!el) return;
      try {
        el.muted = true; el.defaultMuted = true; el.setAttribute("muted", "");
        el.playsInline = true; el.setAttribute("playsinline", "");
      } catch (e) {}
      const p = el.play();
      if (p && p.catch) p.catch(() => { timers.push(window.setTimeout(() => { try { el.play().catch(() => {}); } catch (e) {} }, 220)); });
    };

    const finishNow = () => {
      if (done) return;
      done = true;
      window.cancelAnimationFrame(raf);
      setFading(true);
      cbRef.current.onReveal && cbRef.current.onReveal();
      timers.push(window.setTimeout(() => { cbRef.current.onFinish && cbRef.current.onFinish(); }, REVEAL));
    };

    const tick = () => {
      if (done) return;
      if (v && v.readyState >= 2) {
        const dur = (v.duration && isFinite(v.duration)) ? v.duration : FULL_DUR;
        if (v.currentTime >= dur - END_LEAD) { finishNow(); return; }
      }
      raf = window.requestAnimationFrame(tick);
    };

    play(v);
    if (v) v.addEventListener("ended", finishNow);
    raf = window.requestAnimationFrame(tick);
    timers.push(window.setTimeout(finishNow, MASTER_MAX));

    skipRef.current = () => { try { v && v.pause(); } catch (e) {} finishNow(); };

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      window.cancelAnimationFrame(raf);
      if (v) v.removeEventListener("ended", finishNow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  if (!isActive) return null;

  return (
    <>
      <section
        data-testid="hero-eye"
        className="fixed inset-0 z-[9998] overflow-hidden pointer-events-none"
      >
        <div
          className="absolute inset-0"
          style={{
            opacity: fading ? 0 : 1,
            transition: `opacity ${fading ? REVEAL : SKIP_FADE}ms ease`,
            backgroundColor: "#010101",
            backgroundImage: `url(${pub}/intro-poster.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <video
            ref={vRef}
            data-testid="intro-video"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            poster={`${pub}/intro-poster.jpg`}
            autoPlay
            muted
            playsInline
            preload="auto"
          >
            <source src={`${pub}/intro-full.mp4`} type="video/mp4" />
          </video>

          {/* cinematic vignette */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: "radial-gradient(120% 95% at 50% 42%, transparent 52%, rgba(0,0,0,0.55) 100%)" }}
          />
        </div>
      </section>

      {!fading && (
        <button
          type="button"
          data-testid="intro-skip"
          onClick={() => skipRef.current && skipRef.current()}
          aria-label="Passer l'intro"
          className="fixed z-[10001] flex items-center gap-1 rounded-full border border-white/20 bg-black/50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/85 backdrop-blur-md pointer-events-auto transition-colors duration-200 hover:bg-black/70 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-perf-excellent focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          style={{
            top: "calc(env(safe-area-inset-top, 0px) + 16px)",
            right: "calc(env(safe-area-inset-right, 0px) + 16px)",
            animation: "hm-skip-in 400ms ease 500ms both",
          }}
        >
          Passer
          <ChevronRight size={15} className="text-gold" />
        </button>
      )}
    </>
  );
};

export default HeroSection;
